import { Link, useParams, Navigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MobileSticky from "../components/MobileSticky";
import Seo, { localBusinessSchema, serviceSchema, breadcrumbSchema } from "../components/Seo";
import { SERVICES, SUBURBS, COMPANY } from "../data/site";
import { ArrowRight, Phone, Check } from "lucide-react";

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/" replace />;

  const details = service.details || [service.short];
  const summary = details[0];
  const featureImage = (service.images && service.images[0]) || service.hero;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = `${origin}/${service.slug}`;

  // Rotate 6 suburbs for internal linking (keeps content lightly unique per service)
  const suburbLinks = SUBURBS.slice(0, 6);

  return (
    <div data-testid={`page-service-${service.slug}`}>
      <Seo
        title={`${service.seoTitle || service.title} | Connolly's Painting Solutions`}
        description={`${service.short} Premium paint systems, careful prep, on-time delivery across the Sunshine Coast — Maroochydore, Noosa, Buderim, Caloundra and surrounds.`}
        path={`/${service.slug}`}
        image={service.hero}
        schema={[
          localBusinessSchema(COMPANY),
          serviceSchema({ name: service.title, description: service.short, url: pageUrl }),
          breadcrumbSchema([
            { name: "Home", url: `${origin}/` },
            { name: service.name, url: pageUrl },
          ]),
        ]}
      />
      <Header />

      {/* COMPACT HERO with prominent CTA */}
      <section className="relative bg-[#0B1426] text-white" style={{ minHeight: "60vh" }}>
        <div className="absolute inset-0">
          <img src={service.hero} alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1426]/40 via-[#0B1426]/55 to-[#0B1426]" />
        </div>
        <div
          className="relative container-cps max-w-4xl py-24 md:py-24"
          style={{ paddingTop: "clamp(96px, 22vw, 192px)", paddingBottom: "clamp(96px, 18vw, 160px)" }}
        >
          <div className="overline">Service</div>
          <h1 className="font-display text-[2.4rem] leading-[1.1] mt-12 md:text-5xl lg:text-6xl md:mt-3 tracking-tight md:leading-[1.05]">{service.title}</h1>
          <div className="hairline-gold mt-10 md:hidden" />
          <p className="hidden md:block mt-5 text-lg text-white/75 max-w-2xl leading-relaxed">{service.short}</p>
          {/* CTAs — desktop only; mobile uses sticky bottom bar */}
          <div className="hidden md:flex mt-7 flex-wrap items-center gap-3" data-testid={`service-hero-ctas-${service.slug}`}>
            <Link to="/quote" className="btn-gold text-base" style={{ padding: "16px 32px" }} data-testid={`service-hero-quote-${service.slug}`}>
              Get a Free Quote <ArrowRight size={16} className="ml-2" />
            </Link>
            <a href={`tel:${COMPANY.phoneTel}`} className="btn-ghost-light" data-testid={`service-hero-call-${service.slug}`}>
              <Phone size={14} className="mr-2" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      {/* CONTENT — text + single feature image, tight */}
      <section className="bg-[#F9F8F6] py-14 md:py-20">
        <div className="container-cps grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="overline">What's involved</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight leading-tight">
              {service.name}, done properly.
            </h2>
            <p className="mt-5 text-[#4F5B6A] leading-relaxed">{summary}</p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {service.bullets.map((b) => (
                <li key={b} className="flex gap-2.5 text-[#0B1426] text-[15px]">
                  <Check size={16} className="text-[#C5A059] flex-shrink-0 mt-1" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/quote" className="btn-gold text-base" style={{ padding: "16px 32px" }} data-testid={`service-mid-quote-${service.slug}`}>
                Get a Free Quote <ArrowRight size={16} className="ml-2" />
              </Link>
            </div>
          </div>
          <div className="aspect-[4/5] overflow-hidden bg-[#EEEAE0] order-first lg:order-last" data-testid={`service-feature-image-${service.slug}`}>
            <img src={featureImage} alt={service.name} loading="lazy" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* AREAS WE SERVE — internal linking for local SEO */}
      <section className="bg-white py-12 md:py-16" data-testid={`service-areas-${service.slug}`}>
        <div className="container-cps">
          <div className="overline">Service areas</div>
          <h2 className="font-display text-2xl md:text-3xl mt-3 tracking-tight text-[#0B1426]">
            {service.name} across the Sunshine Coast.
          </h2>
          <p className="mt-4 text-[#4F5B6A] leading-relaxed max-w-3xl text-[15px]">
            We handle {service.name.toLowerCase()} projects throughout the Sunshine Coast — from Noosa in the north through to Caloundra in the south. Click through to a suburb near you:
          </p>
          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[15px]">
            {suburbLinks.map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4"
                data-testid={`service-area-link-${s.slug}`}
              >
                Painters {s.name}
              </Link>
            ))}
            <Link
              to="/suburbs"
              className="text-[#4F5B6A] hover:text-[#C5A059] text-[13px] uppercase tracking-[0.14em] pt-[3px]"
            >
              All areas →
            </Link>
          </div>

          {/* Related services */}
          <div className="mt-10 pt-8 border-t border-[#E5E3DB]">
            <div className="overline">Related services</div>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[15px]">
              {SERVICES.filter((x) => x.slug !== service.slug).map((x) => (
                <Link
                  key={x.slug}
                  to={`/${x.slug}`}
                  className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4"
                  data-testid={`related-service-${x.slug}`}
                >
                  {x.name}
                </Link>
              ))}
              {service.slug === "commercial-painting" && (
                <Link
                  to="/body-corporate-painters-sunshine-coast"
                  className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4 font-medium"
                  data-testid="related-service-body-corporate"
                >
                  Body Corporate Painting →
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA — compact band */}
      <section className="bg-[#0B1426] text-white py-12 md:py-16" data-testid={`service-cta-${service.slug}`}>
        <div className="container-cps grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-7">
            <div className="overline" style={{ color: "rgba(255,255,255,0.7)" }}>Free quote today</div>
            <h2 className="font-display text-2xl md:text-3xl mt-2 tracking-tight">
              Ready to start your {service.name.toLowerCase()}?
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-wrap md:justify-end items-center gap-3">
            <Link to="/quote" className="btn-gold text-base" style={{ padding: "16px 32px" }} data-testid={`service-get-quote-${service.slug}`}>
              Get a Free Quote <ArrowRight size={16} className="ml-2" />
            </Link>
            <a href={`tel:${COMPANY.phoneTel}`} className="btn-ghost-light" data-testid={`service-call-${service.slug}`}>
              <Phone size={14} className="mr-2" /> Call
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <MobileSticky />
    </div>
  );
}
